/* ══ v18: THE BOOT CHAIN — session, then the portfolio, then the first paint ══════════════════
   Replaces v14's identity → parts → merge chain. The order still matters and for the same reasons:

     1. THE SESSION FIRST, and AWAITED. The writer id (the username) decides which id block new
        records are minted in, so nothing may mint before it is known. No session → the login
        dialog, and boot waits for it; no portfolio data is requested before a login succeeds.
     2. THE PORTFOLIO, from GET /api/portfolio, with this user's preferences folded in.
     3. THE ADOPT, which routes. _adoptDB gates the schema, normalises, backfills uids, takes the
        rev baseline and calls route() — exactly as it did for a merged portfolio.
     4. THE RESUME, after the adopt rather than racing it (see _restoreLastProgrammeAndView).

   ⚠ NOTHING PAINTS BEFORE THE ADOPT, as before: an empty seed flashing "no projects" over real
   data is the most alarming possible boot defect.
   ⚠ A FAILED LOAD BLOCKS SAVE (`_dbLoadOk = false`), so a tab that could not read the portfolio
   can never publish an empty one over it. */
/* ⚠ v14 (W5): a settled-boot signal, because boot is not synchronous. Anything driving this shell
   should wait for this rather than guess a duration. */
let _bootDone = false;
async function _bootFromServer() {
  /* First, and synchronously: the mark is furniture, so it must be right on the very first paint
     rather than swapping a moment later. A no-op while BRAND_MARK matches the markup's default. */
  try { _applyBrandMark(); } catch (e) { console.error('Could not apply the brand mark:', e); }

  let status = null;
  while (!status) {
    try { status = await _api('GET', '/api/auth-status'); }
    catch (e) {
      _srvLoadError = e.message;
      /* Server down at boot: say so and retry; there is nothing else useful to show. */
      _dbLoadOk = false;
      route();
      _repaintFileState();
      await new Promise(r => setTimeout(r, 5000));
    }
  }
  _srvHasUsers = !!status.hasUsers;
  _srvUser = status.user || null;
  if (!_srvUser) _srvUser = await _srvWaitForLogin();
  _writerUser = _srvUser.username;
  _reseedMintCounters();
  _srvPaintUserChips();
  if (_srvUser.role === 'admin') _srvLoadUsers().catch(() => {});

  let got = null;
  try { got = await _srvFetchPortfolio(); }
  catch (e) { _srvLoadError = 'The server could not provide the portfolio (' + ((e && e.message) || e) + ').'; }

  /* The programme title is gated until the real portfolio is in (see _progTitleReady), so it
     cannot flash the empty seed's name. v17.6 opened the gate when the handle restore settled. */
  _progTitleReady = true;
  /* v17.5 P4: the personal list order is safe to read once we know whose it is. */
  _listOrderEnsureBinding();
  if (got) {
    try {
      _adoptDB(got.db);                       /* gates, normalises, baselines, and routes */
      _srvChangeSeq = got.changeSeq;
      _srvLoadError = null;
      await _restoreLastProgrammeAndView();
      _repaintFileState();
      _bootDone = true;
      _srvStartPolling();
      return;
    } catch (e) {
      _srvLoadError = 'The portfolio could not be opened (' + ((e && e.message) || e) + ').';
    }
  }
  _dbLoadOk = false;
  route();
  await _restoreLastProgrammeAndView();
  _repaintFileState();
  _bootDone = true;
  _srvStartPolling();
}
/* All late lexical file/viewer flags now exist. Enable P7 only here; the earlier dashboard pass
   deliberately rendered an inert row and never consulted those flags. */
_progressControllerReady = true;
_initProjectProgressController();
_wbsReorderReady = true;
_initWbsReorderController();
renderWorkstreams();
renderOverallProgress();
_bootFromServer();
